import React, { useState, useContext, useEffect } from 'react';
import { Modal } from "reactstrap";
import Header from '../components/Header';
import SearchResult from './SearchResult';
import '../style/body-style.css';
import '../style/search-box.css';
import CK_en from "../resources/CK_en.json";
import CK_fr from '../resources/CK_fr.json';
import LanguageContext from '../components/LanguageContext';
import Footer from '../components/Footer';
import DOMPurify from 'dompurify';
import {getBaseUrl} from "../components/Utils";

function Home() {
  const [modalRegisteredNameOpen, setModalRegisteredNameOpen] = useState(false);
  const [modalOCNOpen, setModalOCNOpen] = useState(false);
  const [modalBINOpen, setModalBINOpen] = useState(false);
  const [exactMatch, setExactMatch] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [searchResultOpen, setSearchResultOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [dataResponse, setDataResponse] = useState([]);
  const [pageSize, setPageSize] = useState(10);
  const { language } = useContext(LanguageContext);
  const [searchError, setSearchError] = useState('');
  const [displayInenigible, setDisplayInenigible] = useState(false);

  const handleModalRegisteredNameOpen = () => {
    setModalRegisteredNameOpen(true);
  };

  const handleModalOCNOpen = () => {
    setModalOCNOpen(true);
  };

  const handleModalBINOpen = () => {
    setModalBINOpen(true);
  };

  const LoadingIndicator = () => (
    <div className="loading-indicator-container">
      <div className="ontario-loading-indicator">
        <svg className="ontario-loading-indicator__spinner" viewBox="25 25 50 50" xmlns="http://www.w3.org/2000/svg">
          <circle cx="50" cy="50" r="20" fill="none" />
        </svg>
        <p>Loading</p>
      </div>
    </div>
  );

  const handleExactMatchChange = (event) => {
    setExactMatch(event.target.checked);
  };

  const handleOnChangeText = (event) => {
    setSearchText(event.target.value);
    setSearchError('');
  };

  const CK = language === 'en' ? CK_en : CK_fr;
  document.title = CK['entity_search.title'];

  const handleOnSubmit = async (event) => {
    event.preventDefault();
    setDisplayInenigible(false);

    if (searchText.trim() === '') {
      setSearchError(CK["entity_search.error.null_search"]);
      return;
    }

    setIsLoading(true);

    // Clear any previous search results and search text from sessionStorage
    sessionStorage.removeItem('Home');
    sessionStorage.removeItem('searchText');

    const controller = new AbortController();
    const { signal } = controller;

    const fetchData = async () => {
        try {
            await new Promise(resolve => setTimeout(resolve, 1000)); // 1 second delay for user experience
            const response = await fetch(getBaseUrl(process.env.REACT_APP_ENV) + '/searchEntities', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    searchFor: searchText,
                    exactMatch: exactMatch
                }),
                signal // Pass the signal to the fetch request
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();

            // Store the new search results and search text in sessionStorage
            sessionStorage.setItem('Home', JSON.stringify(data));
            sessionStorage.setItem('searchText', searchText);

            setDataResponse(data);
            setSearchResultOpen(true);
            } catch (error) {
                if (error.name === 'AbortError') {
                    console.log('Fetch request was aborted');
                } else {
                    console.error('Error fetching data:', error);
                    setSearchResultOpen(true);
                }
            } finally {
                setIsLoading(false);
            }
        };

        const timeout = new Promise((_, reject) => {
            setTimeout(() => {
                controller.abort(); // Abort the fetch request
                reject(new Error('The request is taking longer than expected.'));
            }, 30000); // 30 seconds
        });

        try {
            await Promise.race([fetchData(), timeout]);
        } catch (error) {
            console.error(error.message);
            setIsLoading(false);
            setSearchResultOpen(false);
            alert('The request is taking longer than expected. Please try again later.');
        }
    };

  const handleReset = () => {
    setDisplayInenigible(false);
    setSearchText('');
    setSearchError('');
    setExactMatch(false);
    setDataResponse([]);
    setSearchResultOpen(false);

    // Clear the search results and search text from sessionStorage when the form is reset
    sessionStorage.removeItem('Home');
    sessionStorage.removeItem('searchText');
  };

  useEffect(() => {
    const storedResults = sessionStorage.getItem('Home');
    const storedSearchText = sessionStorage.getItem('searchText');
    if (storedResults) {
        setDataResponse(JSON.parse(storedResults));
        setSearchResultOpen(true);
    }
    if (storedSearchText) {
        setSearchText(storedSearchText);
    }
    
    if (searchText.trim() === '' && searchError) {
      setSearchError(CK["entity_search.error.null_search"]);
    }
  }, [language]);


  const helpContent = CK["entity_search.options.name.help"];
  const sanitizedHtml = DOMPurify.sanitize(helpContent);

  const helpContent2 = CK["entity_search.options.ocn.help"];
  const sanitizedHtml2 = DOMPurify.sanitize(helpContent2);

  const helpContent3 = CK["entity_search.options.bin.help"];
  const sanitizedHtml3 = DOMPurify.sanitize(helpContent3);

  return (
    <div className="home-container">
      <Header />
      <div id="entity-search__container" className="ontario-container">
          <span className="ontario-label--heading" tabIndex={0} style={{ marginBottom: '1rem', display: 'block' }}>
              {CK["entity_search.header"]}
          </span>
        <div className="ontario-search" tabIndex={0}>
          {CK["entity_search.options.label"]}<strong>:</strong>
        </div>
        <ul className="ulTag">
          <li className="listItems">
            <span className="dot">
              {CK["entity_search.options.name"]}
            </span>
            <img
              className="question-img"
              src="images/ontario-icon-help.png"
              alt={CK['entity_search.options.name']}
              onClick={handleModalRegisteredNameOpen}
              tabIndex="0"
              role="button"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleModalRegisteredNameOpen();
                }
              }}
            />
          </li>
          <li className="listItems">
            <span className="dot">
              {CK["entity_search.options.ocn"]}
            </span>
            <img
              className="question-img"
              src="images/ontario-icon-help.png"
              alt={CK['entity_search.options.ocn']}
              onClick={handleModalOCNOpen}
              tabIndex="0"
              role="button"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleModalOCNOpen();
                }
              }}
            />
          </li>
          <li className="listItems">
            <span className="dot">
              {CK["entity_search.options.bin"]}
            </span>
            <img
              className="question-img"
              src="images/ontario-icon-help.png"
              alt={CK['entity_search.options.bin']}
              onClick={handleModalBINOpen}
              tabIndex="0"
              role="button"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleModalBINOpen();
                }
              }}
            />
          </li>
        </ul>
        <div className="ontario-row">
          <form onSubmit={handleOnSubmit} className="ontario-search__container" role="search" noValidate>
            {searchError && (
              <div className="ontario-error-messaging" role="alert">
                <span className="ontario-error-messaging__content">{searchError}</span>
              </div>
            )}
            <div className="ontario-search__input-container">
              <input
                type="search"
                name="search"
                id="ontario-search-input-field"
                autoComplete="off"
                aria-autocomplete="none"
                className={`ontario-search__input ontario-input${searchError ? ' ontario-input__error' : ''}`}
                required
                maxLength="100"
                value={searchText}
                onChange={handleOnChangeText}
                aria-label="Search Text"
              />
              <input
                type="reset"
                className="ontario-search__reset"
                id="ontario-search-reset"
                value=""
                aria-label="Clear search"
                onClick={handleReset}
              />
              <button
                type="submit"
                className="ontario-search__submit"
                id="ontario-search-box__submit"
              >
                <span className="ontario-show-for-sr">Submit</span>
                <svg className="svg-icon" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M15.5 14h-.8l-.28-.27A6.47 6.47 0 0 0 16 9.5a6.5 6.5 0 1 0-13 0A6.5 6.5 0 0 0 9.5 16a6.47 6.47 0 0 0 4.23-1.57l.27.28v.8l5 5 1.5-1.5-5-5zm-6 0C7 14 5 12 5 9.5S7 5 9.5 5 14 7 14 9.5 12 14 9.5 14z"/>
                </svg>
              </button>
            </div>
          </form>
        </div>
        <fieldset className="ontario-checkboxes">
          <div className="ontario-checkboxes__item">
            <input
              className="ontario-input ontario-checkboxes__input"
              name="exact_match"
              id="exact_match"
              type="checkbox"
              checked={exactMatch}
              onChange={handleExactMatchChange}
              tabIndex={0}
            />
            <label htmlFor="exact_match" className="ontario-checkboxes__label info-label">
              {CK["entity_search.exact_match"]}
            </label>
          </div>
        </fieldset>
        <div className="ontario-loading-indicator__container" aria-hidden="true">
          {isLoading && <LoadingIndicator />}
        </div>

        <div className='ontario-row' aria-hidden="true">
          {searchResultOpen && (
            <SearchResult
              results={dataResponse}
              totalRecords={dataResponse.length}
              pageSize={pageSize}
              setPageSize={setPageSize}
              displayInenigible={displayInenigible}
              setDisplayInenigible={setDisplayInenigible}
            />
          )}
        </div>
        
        {/* <Footer /> */}
      </div>
      <Footer />
      {/* Modal for Registered Name */}
      <Modal
        isOpen={modalRegisteredNameOpen}
        toggle={() => setModalRegisteredNameOpen(false)}
      >
        <div className="modal-header">
          <span className="modal-title ontario-h1" id="modal-title-default" tabIndex="0"  style={{ fontWeight: '700', fontSize: '1.75rem', margin: '1.5em 0'}}>
            {CK["entity_search.options.name"]}
          </span>
          <button
            aria-label="Close"
            className="btn-close"
            onClick={() => setModalRegisteredNameOpen(false)}
            type="button"
          >
            <img src="images/ontario-icon-close.svg" alt="Icon close" />
          </button>
        </div>
        <div className="modal-body">
          <ul dangerouslySetInnerHTML={{ __html: sanitizedHtml }} />
        </div>
      </Modal>

      {/* Modal for Ontario Corporation Number (OCN) */}
      <Modal
        isOpen={modalOCNOpen}
        toggle={() => setModalOCNOpen(false)}
      >
        <div className="modal-header">
          <span className="modal-title ontario-h1" id="modal-title-default"  tabIndex="0" style={{fontWeight: '700', fontSize: '1.75rem', margin: '1.5em 0'}}>
            {CK["entity_search.options.ocn"]}
          </span>
          <button
            aria-label="Close"
            className="btn-close"
            onClick={() => setModalOCNOpen(false)}
            type="button"
          >
            <img src="images/ontario-icon-close.svg" alt="Icon close" />
          </button>
        </div>
        <div className="modal-body">
          <ul dangerouslySetInnerHTML={{ __html: sanitizedHtml2 }} />
        </div>
      </Modal>

      {/* Modal for Business Identification Number (BIN) */}
      <Modal
        isOpen={modalBINOpen}
        toggle={() => setModalBINOpen(false)}
      >
        <div className="modal-header">
          <span className="modal-title ontario-h1" id="modal-title-default"  tabIndex="0" style={{fontWeight: '700', fontSize: '1.75rem', margin: '1.5em 0'}}>
            {CK["entity_search.options.bin"]}
          </span>
          <button
            aria-label="Close"
            className="btn-close"
            onClick={() => setModalBINOpen(false)}
            type="button"
          >
            <img src="images/ontario-icon-close.svg" alt="Icon close" />
          </button>
        </div>
        <div className="modal-body">
          <ul dangerouslySetInnerHTML={{ __html: sanitizedHtml3 }} />
        </div>
      </Modal>
    </div>
  );
}

export default Home;
